import type { DocumentCategory } from '@seen/core/models/document-references/constants'
import type { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { Button, cn } from '@seen/ui'
import { type JSX, useEffect, useMemo, useRef, useState } from 'react'
import { HighlightedText } from '#src/components/core/HighlightedText.tsx'
import { SearchMatchSnippet } from '#src/components/core/SearchMatchSnippet.tsx'
import { CategoryBadge } from '#src/components/document-reference/CategoryBadge.tsx'
import { extractDocSearchFields } from '#src/components/document-reference/documentSearchFields.ts'
import { RichText } from '#src/components/RichText.tsx'
import {
  countStemOccurrencesMulti,
  type MatchInfo,
} from '#src/hooks/useClientSideSearch.ts'

/* Document cell: title and categories on top, aiSummary below. Title prefers
 * the AI-generated `description` (stripped to plain text for highlight
 * support); falls back to attachment titles, then `'Untitled document'`.
 * Search results render `HighlightedText`; the non-search path keeps
 * the rich editor rendering for aiSummary. `extractedFields` is the
 * search hook's per-doc cache — avoids re-running `extractTextFromHTML`
 * (DOMParser) per keystroke. */
export function DocumentInfoCell({
  doc,
  match,
  extractedFields,
  activeCategoryFilter,
  onTitleClick,
  readableTitles = false,
  defaultSummaryExpanded = false,
}: {
  doc: ExtendedDocumentReference
  match: MatchInfo | undefined
  extractedFields: Record<string, string> | undefined
  activeCategoryFilter: DocumentCategory | undefined
  onTitleClick: () => void
  readableTitles?: boolean
  defaultSummaryExpanded?: boolean
}): JSX.Element {
  const [summaryExpanded, setSummaryExpanded] = useState(defaultSummaryExpanded)
  const [summaryOverflows, setSummaryOverflows] = useState(false)
  const summaryRef = useRef<HTMLDivElement>(null)
  /* On a category tab, hide the badge for the active category (it's
   * already conveyed by the tab). On All / folder tabs (no active
   * category), show every category the doc carries. */
  const visibleCategories =
    activeCategoryFilter === undefined
      ? doc.categories
      : doc.categories.filter((c) => c !== activeCategoryFilter)
  const terms = match?.terms
  const aiSummary = doc.aiSummary
  const stems = useMemo(() => terms ?? [], [terms])
  // Fallback runs only when the idle index hasn't built yet.
  const fallbackFields = useMemo(
    () => extractedFields ?? extractDocSearchFields(doc),
    [extractedFields, doc]
  )
  const { perField } = useMemo(
    () =>
      stems.length > 0
        ? countStemOccurrencesMulti(fallbackFields, stems)
        : { perField: {} as Record<string, number> },
    [fallbackFields, stems]
  )
  const transcriptionMatches = perField.transcription ?? 0
  /* When description is set, use the HTML-stripped variant so
   * search highlighting matches the indexed text. Otherwise fall
   * through to the model's display title (handles N-attachment,
   * single-attachment, and 'Untitled document' fallback). */
  const strippedDescription = fallbackFields.description.trim()
  const displayTitle = strippedDescription || doc.displayTitle

  useEffect(() => {
    const summary = summaryRef.current
    if (!readableTitles || !aiSummary || !summary) {
      setSummaryOverflows(false)
      return
    }

    const measure = (): void => {
      setSummaryOverflows(
        summaryExpanded || summary.scrollHeight > summary.clientHeight + 1
      )
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(summary)
    return () => observer.disconnect()
  }, [readableTitles, aiSummary, summaryExpanded, displayTitle])

  return (
    <div className="flex flex-col gap-1">
      <div
        className={cn(
          'flex min-w-0 gap-2',
          readableTitles ? 'flex-col items-start gap-1' : 'items-center'
        )}
      >
        <button
          type="button"
          onClick={(e) => {
            /* Don't bubble to DataTable's row `onClick`; the restricted-
             * folder branch of `maybeShowDocumentReference` toasts on
             * every call, so a single click would surface two toasts. */
            e.stopPropagation()
            onTitleClick()
          }}
          className={cn(
            'min-w-0 cursor-pointer truncate text-left font-semibold focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
            readableTitles ? 'w-full' : 'flex-1'
          )}
        >
          {terms && terms.length > 0 ? (
            <HighlightedText text={displayTitle} terms={terms} />
          ) : (
            displayTitle
          )}
        </button>
        {visibleCategories.length > 0 && (
          <div
            className={cn(
              'flex flex-wrap gap-1',
              !readableTitles && 'shrink-0'
            )}
          >
            {visibleCategories.map((category) => (
              <CategoryBadge
                key={category}
                category={category}
                className="w-fit"
              />
            ))}
          </div>
        )}
      </div>
      {aiSummary && (
        <div
          ref={summaryRef}
          aria-label="AI summary"
          className={cn(
            (!readableTitles || !summaryExpanded) && 'line-clamp-3'
          )}
        >
          {terms && terms.length > 0 ? (
            <HighlightedText
              text={fallbackFields.aiSummary}
              terms={terms}
              className="text-sm text-muted-foreground"
            />
          ) : (
            <RichText
              className="text-sm text-muted-foreground"
              value={aiSummary}
            />
          )}
        </div>
      )}
      {readableTitles && summaryOverflows && (
        <Button
          type="button"
          variant="link"
          size="xs"
          className="h-auto self-start p-0 text-xs"
          aria-expanded={summaryExpanded}
          onClick={(event) => {
            event.stopPropagation()
            setSummaryExpanded((expanded) => !expanded)
          }}
        >
          {summaryExpanded ? 'Show less' : 'Show more'}
        </Button>
      )}
      {transcriptionMatches > 0 && (
        <SearchMatchSnippet
          hiddenFieldText={fallbackFields.transcription}
          stemmedTerms={stems}
          hiddenFieldMatches={transcriptionMatches}
        />
      )}
    </div>
  )
}
